import {TicketCreatePrefill} from './ticket-create-context'

/**
 * A request to the sales department in place of «تماس بگیرید» (user request 2026-10-18: «توی تماس بگیرید ها بجای
 * تلفن بشه ثبت تیکت برای بخش فروش و درخواست فعال سازی و ...»): a link to the new-ticket page whose query names the
 * request, and the ticket's prefilled classification, subject and description read back from that query. The query
 * also comes from the website and from messages, so nothing in it is trusted beyond picking a known request.
 */
export type ServiceRequestTopic =
  | 'ServiceActivation'
  | 'AccessRenewal'
  | 'LimitIncrease'
  | 'Pricing'
  | 'WalletCharge'
  | 'Contract'

export const NEW_TICKET_PATH = '/apps/ticket-management/new'

type RequestShape = {
  department: string
  requestType: string
  subject: (label?: string) => string
  description: (label?: string) => string
  priority: 1 | 2 | 3 | 4
}

const named = (label?: string) => (label ? `«${label}»` : 'سرویس ویژه')

const REQUESTS: Record<ServiceRequestTopic, RequestShape> = {
  ServiceActivation: {
    department: 'Sales',
    requestType: 'ServiceRequest',
    subject: (label) => `درخواست فعال‌سازی ${named(label)}`,
    description: (label) =>
      `لطفاً ${label ? `سرویس ${named(label)}` : 'سرویس ویژهٔ موردنظر'} را برای این کسب‌وکار فعال کنید و شرایط و هزینهٔ آن را اعلام کنید.`,
    priority: 2,
  },
  AccessRenewal: {
    department: 'Sales',
    requestType: 'ServiceRequest',
    subject: (label) => `درخواست تمدید دسترسی ${named(label)}`,
    description: (label) =>
      `دسترسی این کسب‌وکار به ${label ? `سرویس ${named(label)}` : 'سرویس ویژه'} رو به پایان است یا به پایان رسیده؛ لطفاً برای تمدید آن اقدام کنید.`,
    priority: 2,
  },
  LimitIncrease: {
    department: 'Sales',
    requestType: 'ServiceRequest',
    subject: (label) => `درخواست افزایش سقف ${named(label)}`,
    description: (label) =>
      `سقف یا سهمیهٔ ${label ? `سرویس ${named(label)}` : 'سرویس'} برای کار این کسب‌وکار کافی نیست؛ لطفاً امکان افزایش آن و هزینه‌اش را اعلام کنید.`,
    priority: 2,
  },
  Pricing: {
    department: 'Sales',
    requestType: 'Question',
    subject: (label) => `استعلام قیمت ${named(label)}`,
    description: (label) => `لطفاً قیمت و شرایط ${label ? `سرویس ${named(label)}` : 'سرویس‌های ویژه'} را اعلام کنید.`,
    priority: 1,
  },
  WalletCharge: {
    department: 'Finance',
    requestType: 'ServiceRequest',
    subject: () => 'درخواست افزایش موجودی کیف پول',
    description: () =>
      'افزایش موجودی کیف پول از درگاه برای این کسب‌وکار ممکن نشد؛ لطفاً راه افزایش موجودی را اعلام کنید.',
    priority: 2,
  },
  Contract: {
    department: 'Sales',
    requestType: 'ServiceRequest',
    subject: () => 'پیگیری قرارداد و اشتراک کسب‌وکار',
    description: () =>
      'قراردادها و اشتراک‌های این کسب‌وکار در پنل دیده نمی‌شود؛ لطفاً کسب‌وکار را به پروندهٔ سازمانش در CRM وصل کنید.',
    priority: 2,
  },
}

/**
 * The topic codes of catalog 2026.10.1 (backend 1.0.0.672+). A backend with an older catalog lacks them: the
 * category picker then takes the nearest topic it has, so a request still goes to the same department.
 */
export const TOPIC_FALLBACKS: Record<string, string> = {
  ServiceActivation: 'BuyService',
  AccessRenewal: 'Renewal',
  LimitIncrease: 'UpgradePlan',
  WalletCharge: 'OtherFinance',
}

const isTopic = (value: string | null): value is ServiceRequestTopic =>
  value !== null && Object.prototype.hasOwnProperty.call(REQUESTS, value)

/** A service's name as the query carries it: one line of plain text, short. */
const cleanLabel = (value: string | null) =>
  (value || '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f«»]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80) || undefined

/** The new-ticket page with the request named: `feature` is the service's key, `label` its name. */
export const serviceRequestPath = (topic: ServiceRequestTopic, options: {feature?: string; label?: string} = {}) => {
  const request = REQUESTS[topic]
  const query = new URLSearchParams({department: request.department, topic})
  if (options.feature) query.set('feature', options.feature)
  const label = cleanLabel(options.label ?? null)
  if (label) query.set('label', label)
  return `${NEW_TICKET_PATH}?${query.toString()}`
}

/**
 * The prefill of a request named in the page's query, or undefined when the query names none (or one this panel does
 * not know). The department always comes from the request itself, not from the query.
 */
export const ticketPrefillFromQuery = (search: string): TicketCreatePrefill | undefined => {
  const query = new URLSearchParams(search)
  const topic = query.get('topic')
  if (!isTopic(topic)) return undefined
  const request = REQUESTS[topic]
  const label = cleanLabel(query.get('label'))
  const feature = (query.get('feature') || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 32)
  return {
    subject: request.subject(label).slice(0, 254),
    description: [request.description(label), feature ? `کد سرویس: ${feature}` : '']
      .filter(Boolean)
      .join('\n'),
    classification: {departmentCode: request.department, requestTypeCode: request.requestType, topicCode: topic},
    priority: request.priority,
  }
}
